import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useContext } from 'react';
import Chatbot from '../chatbot';
import ModalModule from '../modal';
import UI from '../ui';
import Utils from '../utils';
import { useIdentityVerification } from './hooks';

const { WrappedButtonType, InlineMessage } = UI;
const { Analytics } = Utils;

const { ChatbotTriggers, launchChatbot } = Chatbot;
const { Modal, ModalButtonActionType, ModalContext } = ModalModule;

type FundingLimitReachedModalProps = {
	modalId: number;
};

export const FundingLimitReachedModal = ({
	modalId,
}: FundingLimitReachedModalProps) => {
	const { closeModal, showModal } = useContext(ModalContext);
	const { startVerification } = useIdentityVerification();
	const flags = useFlags();

	const startChat = () => {
		closeModal(modalId, ModalButtonActionType.Ok);

		Analytics.trackEvent(
			'live-chat-transfer',
			{
				limit_remaining: 0,
			},
			Analytics.agent.segment
		);

		launchChatbot({ flags, showModal, trigger: ChatbotTriggers.AddFunds });
	};

	const primaryButton = {
		type: ModalButtonActionType.Ok,
		text: 'Verify now',
		onClick: () => {
			closeModal(modalId, ModalButtonActionType.Ok);

			const trackVerificationOutcome = (
				outcome: 'success' | 'failure' | 'aborted'
			) => {
				Analytics.trackEvent(
					'plaid-verification-attempt',
					{
						limit_remaining: 0,
						outcome,
					},
					Analytics.agent.segment
				);
			};

			startVerification({
				onSuccess: () => trackVerificationOutcome('success'),
				onFailure: () => trackVerificationOutcome('failure'),
				onCancel: () => trackVerificationOutcome('aborted'),
			});
		},
		dataqa: 'funding-limit-reached-verify-now-button',
	};

	const secondaryButton = {
		type: ModalButtonActionType.Ok,
		buttonType: WrappedButtonType.TertiaryWhite,
		onClick: startChat,
		text: 'Start chat',
		dataqa: 'funding-limit-reached-start-chat-button',
	};

	return (
		<Modal
			modalId={modalId}
			header="Funding Limit Reached"
			primaryButton={primaryButton}
			secondaryButton={secondaryButton}
			size="small"
		>
			<InlineMessage
				type="error"
				className="funding-restriction-message"
				text={
					<>
						<span>
							You’ve reached your account funding limit. To add more funds to
							your account balance, you must verify your identity.
						</span>
						<span>
							To remove this limit, you can verify your identity using our
							verification tool or chat with a live agent (available
							Monday–Friday 8am - 8pm CT).
						</span>
					</>
				}
				dataqa="funding-limit-reached-inline-message"
			/>
		</Modal>
	);
};
